import {
  Breadcrumb,
  Button,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import BulkUploadCsv from "../../../image/AddCsvFile.png";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../../image/Archiveicon.svg";
import { ReactComponent as Editicon } from "../../../image/Editicon.svg";
import PlusSquareOutlined from "../../../image/PlusSquareOutlined.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import "../../../styles/Main.scss";
import { authUserData } from "../../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import AddMaterial from "./AddMaterial";
import BulkMaterialUpload from "./BulkMaterialUpload";

const MaterialMaster = () => {
  const { t } = useTranslation();
  const { Search } = Input;
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [materialList, setMaterialList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [reload, setReload] = useState(false);
  const [newMaterialAdded, setNewMaterialAdded] = useState(false);
  const [materialTypeId, setMaterialTypeId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [materialMasterId, setMaterialMasterId] = useState(null);
  const [modalKey, setModalKey] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();
  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });
  // Fetch the material list API
  useEffect(() => {
    const getMaterialList = async () => {
      setLoading(true);

      try {
        const fields = [
          "id",
          "code",
          "sub_category",
          "category_tree_name",
          "material_type_name",
          "short_description",
          "unit_of_measurement_name",
        ].join(",");

        const queryParams = new URLSearchParams();
        queryParams.set("fields", fields);
        queryParams.set("page", state.page);
        queryParams.set("page_size", state.page_size);
        queryParams.set("ordering", state.ordering);
        if (state.search) {
          queryParams.set("search", state.search);
        }
        let apiResponse = await getRequest(
          `material-master/?${queryParams.toString()}`,
          state
        );
        if (apiResponse.data.results.success) {
          setMaterialList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_list_material"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getMaterialList();
  }, [state, newMaterialAdded, reload, t]);

  // Function to handle changes in table pagination, filters, and sorting
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // Function to get the appropriate sorting key based on the selected column and order
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "code" && sortOrder === "ascend") {
      sortingEnumKey = "code";
    }
    if (sortField === "code" && sortOrder === "descend") {
      sortingEnumKey = "-code";
    }
    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree__name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree__name";
    }

    if (sortField === "material_type_name" && sortOrder === "ascend") {
      sortingEnumKey = "material_type__type_name";
    }
    if (sortField === "material_type_name" && sortOrder === "descend") {
      sortingEnumKey = "-material_type__type_name";
    }
    if (sortField === "short_description" && sortOrder === "ascend") {
      sortingEnumKey = "short_description";
    }
    if (sortField === "short_description" && sortOrder === "descend") {
      sortingEnumKey = "-short_description";
    }
    if (sortField === "long_description" && sortOrder === "ascend") {
      sortingEnumKey = "long_description";
    }
    if (sortField === "long_description" && sortOrder === "descend") {
      sortingEnumKey = "-long_description";
    }

    if (sortField === "unit_of_measurement" && sortOrder === "ascend") {
      sortingEnumKey = "unit_of_measurement";
    }
    if (sortField === "unit_of_measurement" && sortOrder === "descend") {
      sortingEnumKey = "-unit_of_measurement";
    }

    if (sortField === "image" && sortOrder === "ascend") {
      sortingEnumKey = "image";
    }
    if (sortField === "image" && sortOrder === "descend") {
      sortingEnumKey = "-image";
    }

    return sortingEnumKey;
  };

  // Function to debounce the search callback to reduce API calls
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  // Function to update the search state
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  // Row selection settings for the table
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  // Functions to handle the opening and closing of the bulk upload modal
  const [isModalOpenBulk, setIsModalOpenBulk] = useState(false);
  const showBulkUpload = () => {
    setIsModalOpenBulk(true);
  };
  const handleOkBulk = () => {
    setIsModalOpenBulk(false);
  };
  const handleCancelBulk = () => {
    setIsModalOpenBulk(false);
  };

  // Function to debounce the data loading based on search input
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  // Function to handle archiving materials, either selected or individually by ID
  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("material-master-delete/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids?.length;

        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("material_archive_successfully6"));

        setSelectedRow([]);
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
        ErrorNotificationMsg(t("error_in_delete_material"));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
  };

  // Functions to handle the opening and closing of the material modal
  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
    setFileList([]);
  };
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: true,
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category_tree_name",
      key: "category_tree_name",
      sorter: true,
      width: 200,
    },
     {
      title: "Sub category",
      dataIndex: "sub_category",
      key: "sub_category",
      sorter: true,
      width: 200,
    },
    {
      title: "Material Type",
      dataIndex: "material_type_name",
      key: "material_type_name",
      sorter: true,
      width: 200,
    },

    {
      title: "Short Description",
      dataIndex: "short_description",
      key: "short_description",
      sorter: true,
      width: 200,
      render: (dataIndex) => (
        <Tooltip
          color={colors}
          placement="left"
          title={<span style={{ color: "#fff" }}>{dataIndex}</span>}
        >
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "UoM.",
      dataIndex: "unit_of_measurement_name",
      key: "unit_of_measurement_name",
      sorter: true,
      width: 200,
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 30,
      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="left"
            title={<span style={{ color: "#fff" }}>Edit</span>}
          >
            {authUserData()?.permission.includes(
              "material_master|change_materialmaster"
            ) && (
              <Editicon
                onClick={() => navigate(`/material/edit/${record.id}/`)}
                style={{ cursor: "pointer" }}
              />
            )}
          </Tooltip>
          <Tooltip
            color={colors}
            placement="right"
            title={<span style={{ color: "#fff" }}>Archive</span>}
          >
            <Popconfirm
              className="action"
              title={t("pop_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleArchive(record.id)}
            >
              {authUserData()?.permission.includes(
                "material_master|delete_materialmaster"
              ) && <Archive style={{ cursor: "pointer" }} />}
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];

  // Function to handle updating the material
  const handleUpdate = (id) => {
    setModalKey((prevKey) => !prevKey);
    setMaterialMasterId(id);
    showModal();
  };

  // Function to handle adding a new materialManage
  const handleAdd = () => {
    setMaterialMasterId(null);
    showModal();
    setState({ ...state, ordering: "id" });
    setReload((reload) => !reload);
  };
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{t("pl_materialListing")}</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("materialMaster")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    debounceLoadData(value, onSearch);
                  }}
                  allowClear
                  onSearch={(value) => {
                    setSearchText(value);
                    onSearch(value);
                  }}
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  style={{ width: 250 }}
                />
              </Space>
            </div>
            {authUserData()?.permission.includes(
              "material_master|add_materialmaster"
            ) && (
              <Tooltip
                color={colors}
                placement="right"
                title={
                  <span style={{ color: "#fff" }}>
                    Add a new material with detailed specifications and
                    information.
                  </span>
                }
              >
                <Button
                  className="BlueShadowBtn"
                  type="primary"
                  // onClick={handleAdd}
                  onClick={() => navigate(`/material/add`)}
                  icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
                >
                  {t("pl_add_material")}
                </Button>
              </Tooltip>
            )}
            <Modal
              className="ModelPopupSecMaterialMaster"
              title={materialMasterId ? t("Edit Material") : t("Add Material")}
              open={isModalOpen}
              onOk={handleOk}
              onCancel={handleCancel}
              closable={false}
              wrapClassName="modal-wrapper"
              keyboard={false}
            >
              <AddMaterial
                fileList={fileList}
                setFileList={setFileList}
                reload={reload}
                setReload={setReload}
                handleCancel={handleCancel}
                materialMasterId={materialMasterId}
                modalKey={modalKey}
                setStateM={setState}
              />
            </Modal>
            <Modal
              className="ModelPopupSec"
              title="Upload File"
              open={isModalOpenBulk}
              onOk={handleOkBulk}
              onCancel={handleCancelBulk}
              closable={false}
              wrapClassName="modal-wrapper"
            >
              <BulkMaterialUpload
                setNewMaterialAdded={setNewMaterialAdded}
                newMaterialAdded={newMaterialAdded}
                handleCancelBulk={handleCancelBulk}
                materialTypeId={materialTypeId}
              />
            </Modal>

            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "material_master|delete_materialmaster"
                ) && (
                  <ButtonField
                    type="primary"
                    className="BlueShadowBtn"
                    htmlType="button"
                    label={t("pl_archive")}
                  />
                )}
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="View Archived Records">
              <Link to="/material/archive" className="Archiveicon">
                <Archiveicon />
              </Link>
            </Tooltip>

            <Tooltip placement="topLeft" title="Bulk Upload">
              <Link className="BulkFileUplodIcon" onClick={showBulkUpload}>
                <img src={BulkUploadCsv} alt="" />
              </Link>
            </Tooltip>
          </Space>
        </div>
      </div>

      <section className="grid-sec tablegridsec">
        <div className="PR-body">
          <Row className="tblheader">
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-heading"
            ></Col>
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-action"
            ></Col>
          </Row>
          <div className="tablewrp">
            <Table
              rowSelection={rowSelection}
              columns={columns}
              rowKey={(record) => record.id}
              dataSource={materialList}
              loading={loading}
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showTotal: (total) => `Total ${total} items`,
                showSizeChanger: true,
                locale: {
                  items_per_page: "Items / Page", // Text for the size changer
                },
              }}
              onChange={handleTableChange}
            />
          </div>
        </div>
      </section>
    </>
  );
};
export default MaterialMaster;
